import { useEffect, useCallback, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { X, LogIn, Loader2, Search } from 'lucide-react'
import RoomCard from '../Room/RoomCard'
import { RoomGroup } from '../Room/RoomGroup'
import {
  fetchStudentRoomsList,
  joinRoomByCode,
} from '@/redux/asyncThunks/studentRoom'
import { AppDispatch, RootState } from '@/redux/store'
import { CardType, GameRoomStatus, Status } from '@/redux/types/room'
import StudentDashboardSkeleton from './skeletons/StudentDashboardSkeleton'
import ROUTES from '@/routes/routes'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import RoomNotFound from '../PageNotFound/RoomNotFound'

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [inviteCode, setInviteCode] = useState('')
  const [isJoining, setIsJoining] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('')

  const { rooms, isLoading, error } = useSelector(
    (state: RootState) => state.studentRoom
  )

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    dispatch(fetchStudentRoomsList(debouncedSearchQuery))
  }, [dispatch, debouncedSearchQuery])

  const handleEnterRoom = useCallback(
    (roomid: string, invitationStatus: Status) => {
      const room = rooms.find((r) => r.roomid === roomid)
      const invite_code = room?.invite_code
      if (invitationStatus !== Status.Accepted) {
        navigate(ROUTES.STUDENT.JOIN_ROOM(roomid))
        return
      }
      // A Charter Class has no game, so the game waiting room would strand the
      // student there — the class landing routes them to their charter instead.
      navigate(
        room?.is_charter_only
          ? ROUTES.STUDENT.CHARTER_CLASS(roomid, invite_code)
          : ROUTES.STUDENT.WAITING_ROOM(roomid, invite_code)
      )
    },
    [rooms, navigate]
  )

  const handleJoinWithInviteCode = async () => {
    if (!inviteCode.trim()) {
      alert('Please enter an invite code')
      return
    }

    setIsJoining(true)

    try {
      const result = await dispatch(
        joinRoomByCode({ inviteCode: inviteCode.trim() })
      )

      if (result.payload.room.roomid) {
        const room = result.payload.room
        const code = room.invite_code || inviteCode.trim()
        // A Charter Class has no game, so the game waiting room would strand
        // the student. Send them to the class landing instead.
        navigate(
          room.is_charter_only
            ? ROUTES.STUDENT.CHARTER_CLASS(room.roomid, code)
            : ROUTES.STUDENT.WAITING_ROOM(room.roomid, code)
        )
      } else {
        const errorMessage =
          result.payload ||
          'Failed to join room. Please check the invite code and try again.'
        alert(errorMessage)
      }
    } catch (error) {
      console.error('Error joining room:', error)
      alert('Failed to join room. Please try again.')
    } finally {
      setIsJoining(false)
      setInviteCode('')
    }
  }

  const handleRefresh = useCallback(() => {
    dispatch(fetchStudentRoomsList(debouncedSearchQuery))
  }, [dispatch, debouncedSearchQuery])

  const roomGroups = useMemo(() => {
    // Charter Classes have no game status, so grouping them by IN_PROGRESS /
    // NOT_STARTED / COMPLETED is meaningless — they get their own group.
    const classes = rooms.filter((r) => r.is_charter_only)
    const games = rooms.filter((r) => !r.is_charter_only)
    const live = games.filter(
      (r) =>
        r.status === GameRoomStatus.IN_PROGRESS ||
        r.status === GameRoomStatus.PAUSED
    )
    const waiting = games.filter(
      (r) => r.status === GameRoomStatus.NOT_STARTED
    )
    const completed = games.filter(
      (r) => r.status === GameRoomStatus.COMPLETED
    )
    return { classes, live, waiting, completed }
  }, [rooms])

  const renderCard = useCallback(
    (room: (typeof rooms)[0]) => (
      <RoomCard
        cardType={CardType.ROOM}
        key={room.roomid}
        roomId={room.roomid}
        inviteCode={room.invite_code}
        roomName={room.roomname || undefined}
        showBadge={true}
        status={room.status}
        buttonText='Enter Room'
        onButtonClick={() =>
          handleEnterRoom(room.roomid, room.invitation_status)
        }
        isProfessor={false}
        advisorsCount={room.advisors_count || 0}
        playersCount={room.players_count || 0}
        isSimulating={room.is_simulating}
      />
    ),
    [handleEnterRoom]
  )

  return (
    <div className='w-full min-h-full px-24 py-4'>
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-2 mb-4'>
          <h5 className='text-2xl text-dark-blue'>
            Enter a Room to Get Started!
          </h5>
          <div
            onClick={handleRefresh}
            className='p-1 bg-transparent rounded-md cursor-pointer'
          >
            <img src='/icons/refresh.svg' alt='Refresh' className='size-5' />
          </div>
        </div>

        <div className='flex items-center gap-3'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <Input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search by room name or ID'
              className='w-64 pl-10 pr-10'
            />
            {searchQuery && (
              <Button
                onClick={() => setSearchQuery('')}
                variant='ghost'
                size='sm'
                className='absolute right-1 top-1/2 -translate-y-1/2 p-1 h-auto hover:bg-transparent'
                title='Clear search'
              >
                <X className='w-4 h-4 text-gray-400 hover:text-gray-600' />
              </Button>
            )}
          </div>

          <Card className='shadow-sm hover:shadow-md transition-all duration-200'>
            <CardContent className='p-1'>
              <div className='flex items-center gap-2'>
                <div className='relative flex-1'>
                  <Input
                    type='text'
                    value={inviteCode}
                    onChange={(e) =>
                      setInviteCode(e.target.value.toUpperCase())
                    }
                    placeholder='Enter code'
                    className='w-52 px-3 py-2 text-sm font-medium border-0 focus-visible:ring-0 focus-visible:ring-offset-0'
                    disabled={isJoining}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && inviteCode.trim()) {
                        handleJoinWithInviteCode()
                      }
                    }}
                  />
                  {inviteCode && (
                    <Button
                      onClick={() => setInviteCode('')}
                      variant='ghost'
                      size='sm'
                      className='absolute right-1 top-1/2 -translate-y-1/2 p-1 h-auto hover:bg-transparent'
                      title='Clear'
                    >
                      <X className='w-3 h-3 text-gray-400 hover:text-gray-600' />
                    </Button>
                  )}
                </div>
                <Button
                  onClick={handleJoinWithInviteCode}
                  disabled={isJoining || !inviteCode.trim()}
                  className='px-4 py-2 text-sm font-medium bg-dark-blue hover:bg-dark-blue-hovered disabled:from-gray-400 disabled:to-gray-400 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200'
                >
                  {isJoining ? (
                    <div className='flex items-center gap-1'>
                      <Loader2 className='w-3 h-3 animate-spin' />
                      <span>Joining</span>
                    </div>
                  ) : (
                    <div className='flex items-center gap-1'>
                      <LogIn className='w-3 h-3' />
                      <span>Join</span>
                    </div>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {error ? (
        <div className='flex items-center justify-center w-full h-full text-center mt-7'>
          <RoomNotFound message='Unable to fetch the rooms.' />
        </div>
      ) : isLoading ? (
        <StudentDashboardSkeleton />
      ) : Array.isArray(rooms) && rooms.length > 0 ? (
        <div className='mt-7'>
          <RoomGroup
            title='Charter Classes'
            count={roomGroups.classes.length}
            dotColor='indigo'
            defaultOpen={true}
          >
            {roomGroups.classes.map(renderCard)}
          </RoomGroup>
          <RoomGroup
            title='Live Games'
            count={roomGroups.live.length}
            dotColor='emerald'
            defaultOpen={true}
          >
            {roomGroups.live.map(renderCard)}
          </RoomGroup>
          <RoomGroup
            title='Waiting'
            count={roomGroups.waiting.length}
            dotColor='sky'
            defaultOpen={true}
          >
            {roomGroups.waiting.map(renderCard)}
          </RoomGroup>
          <RoomGroup
            title='Completed'
            count={roomGroups.completed.length}
            dotColor='slate'
            defaultOpen={false}
          >
            {roomGroups.completed.map(renderCard)}
          </RoomGroup>
        </div>
      ) : (
        <p className='text-lg text-dark-blue'>No Rooms</p>
      )}
    </div>
  )
}

export default StudentDashboard
